import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "../api.js";
import type { EventStatus } from "@mma/shared";
import { EVENT_STATUS_LABELS, EventStatusBadge } from "../components/EventStatusBadge.js";

// Fighter-profil: følger personen på tværs af events.
interface FighterProfile {
  id: string;
  name: string;
  club: string | null;
  country: string | null;
  heightCm: number | null;
  reachCm: number | null;
}

interface FightRow {
  id: string;
  orderIndex: number;
  fighterA: FighterProfile;
  fighterACorner: "red" | "blue";
  fighterARecord: string | null;
  fighterB: FighterProfile;
  fighterBCorner: "red" | "blue";
  fighterBRecord: string | null;
  weightClass: string | null;
  totalRounds: number;
  roundDurationSec: number;
  status: "scheduled" | "live" | "finished";
  _count?: { actions: number };
  // Fra import af arrangørens ark
  startTime: string | null;
  level: "amateur" | "pro" | null;
  sponsor: string | null;
  weightLimitKg: number | null;
  fighterAWeighInKg: number | null;
  fighterBWeighInKg: number | null;
}

// Til forslag i kampformularen
interface FighterOption {
  id: string;
  name: string;
  club: string | null;
  country: string | null;
  fightCount: number;
}

const sameName = (a: string, b: string) =>
  a.trim().toLowerCase().replace(/\s+/g, " ") === b.trim().toLowerCase().replace(/\s+/g, " ");

// "175 cm · 177 cm rækkevidde · 70,3 kg" for den ene fighter, tom hvis intet er importeret.
function tapeLine(height: number | null, reach: number | null, weighIn: number | null) {
  return [
    height && `${height} cm`,
    reach && `${reach} cm rækkevidde`,
    weighIn && `${String(weighIn).replace(".", ",")} kg`,
  ]
    .filter(Boolean)
    .join(" · ");
}

interface EventDetail {
  id: string;
  name: string;
  date: string;
  location: string | null;
  status: EventStatus;
  fights: FightRow[];
}

type EventForm = {
  name: string;
  date: string;
  location: string;
  status: EventStatus;
};

// ISO → "YYYY-MM-DDTHH:mm" i lokal tid, som <input type="datetime-local"> forventer.
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60_000)
    .toISOString()
    .slice(0, 16);
}

// Indvejning vises og tastes med komma.
const kg = (n: number | null) => (n == null ? "" : String(n).replace(".", ","));
function parseKg(s: string): number | null {
  const n = Number(s.trim().replace(",", "."));
  return s.trim() === "" || !Number.isFinite(n) ? null : n;
}

type FightForm = {
  fighterAName: string;
  fighterACorner: "red" | "blue";
  fighterACountry: string;
  fighterAClub: string;
  fighterARecord: string;
  fighterAWeighIn: string;
  fighterBName: string;
  fighterBCorner: "red" | "blue";
  fighterBCountry: string;
  fighterBClub: string;
  fighterBRecord: string;
  fighterBWeighIn: string;
  weightClass: string;
  totalRounds: number;
  roundDurationSec: number;
};

const emptyFight: FightForm = {
  fighterAName: "",
  fighterACorner: "blue",
  fighterACountry: "",
  fighterAClub: "",
  fighterARecord: "",
  fighterAWeighIn: "",
  fighterBName: "",
  fighterBCorner: "red",
  fighterBCountry: "",
  fighterBClub: "",
  fighterBRecord: "",
  fighterBWeighIn: "",
  weightClass: "",
  totalRounds: 3,
  roundDurationSec: 300,
};

export function AdminEventPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [ev, setEv] = useState<EventDetail | null>(null);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [form, setForm] = useState<FightForm>(emptyFight);
  const [eventForm, setEventForm] = useState<EventForm | null>(null);
  const [fighters, setFighters] = useState<FighterOption[]>([]);

  async function load() {
    if (!id) return;
    setEv(await api.get<EventDetail>(`/api/events/${id}`));
  }
  useEffect(() => {
    load();
  }, [id]);

  async function loadFighters() {
    setFighters(await api.get<FighterOption[]>("/api/fighters"));
  }

  function startNew() {
    setForm(emptyFight);
    setEditing("new");
    loadFighters();
  }
  function startEdit(f: FightRow) {
    setForm({
      fighterAName: f.fighterA.name ?? "",
      fighterACorner: f.fighterACorner,
      fighterACountry: f.fighterA.country ?? "",
      fighterAClub: f.fighterA.club ?? "",
      fighterARecord: f.fighterARecord ?? "",
      fighterAWeighIn: kg(f.fighterAWeighInKg),
      fighterBName: f.fighterB.name ?? "",
      fighterBCorner: f.fighterBCorner,
      fighterBCountry: f.fighterB.country ?? "",
      fighterBClub: f.fighterB.club ?? "",
      fighterBRecord: f.fighterBRecord ?? "",
      fighterBWeighIn: kg(f.fighterBWeighInKg),
      weightClass: f.weightClass ?? "",
      totalRounds: f.totalRounds,
      roundDurationSec: f.roundDurationSec,
    });
    setEditing(f.id);
    loadFighters();
  }

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!id || !ev) return;
    // Fighteren findes på navn eller oprettes; klub og land opdaterer profilen.
    const fighter = (name: string, club: string, country: string) => ({
      name: name.trim(),
      club: club.trim() || null,
      country: country.trim() || null,
    });
    const payload = {
      eventId: id,
      // Kun ved oprettelse — sendt ved redigering flyttede den kampen til bunden.
      ...(editing === "new" ? { orderIndex: ev.fights.length } : {}),
      fighterA: fighter(form.fighterAName, form.fighterAClub, form.fighterACountry),
      fighterACorner: form.fighterACorner,
      fighterARecord: form.fighterARecord || null,
      fighterAWeighInKg: parseKg(form.fighterAWeighIn),
      fighterB: fighter(form.fighterBName, form.fighterBClub, form.fighterBCountry),
      fighterBCorner: form.fighterBCorner,
      fighterBRecord: form.fighterBRecord || null,
      fighterBWeighInKg: parseKg(form.fighterBWeighIn),
      weightClass: form.weightClass || null,
      totalRounds: form.totalRounds,
      roundDurationSec: form.roundDurationSec,
    };
    try {
      if (editing === "new") {
        await api.post("/api/fights", payload);
      } else if (editing) {
        await api.patch(`/api/fights/${editing}`, payload);
      }
    } catch (err) {
      alert(errorMessage(err, "Kampen kunne ikke gemmes"));
      return;
    }
    setEditing(null);
    load();
  }

  async function activate(fId: string) {
    await api.post(`/api/fights/${fId}/activate`);
    load();
  }

  async function finish(fId: string) {
    const method = prompt(
      "Resultat-metode (ko, tko, submission, decision_unanimous, decision_split, decision_majority, draw, dq, no_contest)",
      "decision_unanimous",
    );
    if (!method) return;
    const winner = prompt("Vinder (a/b)?", "a");
    const round = Number(prompt("I hvilken runde?", "3") ?? "0");
    await api.post(`/api/fights/${fId}/finish`, { method, winner, round });
    load();
  }

  async function del(fId: string) {
    if (!confirm("Slet denne kamp? Alle stats slettes med.")) return;
    await api.delete(`/api/fights/${fId}`);
    load();
  }

  async function reset(fId: string) {
    const ok = confirm(
      "Nulstil denne kamp?\n\nAlle registrerede stats, runde-timer og resultat slettes. Selve kampen og fighter-info bevares.\n\nDenne handling kan ikke fortrydes.",
    );
    if (!ok) return;
    await api.post(`/api/fights/${fId}/reset`);
    load();
  }

  function startEditEvent() {
    if (!ev) return;
    setEventForm({
      name: ev.name,
      date: toLocalInput(ev.date),
      location: ev.location ?? "",
      status: ev.status,
    });
  }

  async function saveEvent(e: React.FormEvent) {
    e.preventDefault();
    if (!ev || !eventForm || !eventForm.name.trim() || !eventForm.date) return;
    await api.patch(`/api/events/${ev.id}`, {
      name: eventForm.name.trim(),
      date: new Date(eventForm.date).toISOString(),
      location: eventForm.location.trim() || null,
      status: eventForm.status,
    });
    setEventForm(null);
    load();
  }

  // Sletning cascader til alle kampe og actions. Har eventet registreret
  // data, skal navnet skrives for at bekræfte — et fejlklik må ikke koste
  // en hel gallas stats.
  async function deleteEvent() {
    if (!ev) return;
    const fights = ev.fights.length;
    const actions = ev.fights.reduce((n, f) => n + (f._count?.actions ?? 0), 0);
    const hasLive = ev.fights.some((f) => f.status === "live");

    const lines = [
      `Slet "${ev.name}"?`,
      "",
      fights === 0
        ? "Eventet har ingen kampe."
        : `${fights} ${fights === 1 ? "kamp" : "kampe"} og ${actions} registrerede actions slettes med.`,
    ];
    if (hasLive) lines.push("", "⚠ En kamp er LIVE — vMix-grafikkerne bliver tomme.");
    lines.push("", "Denne handling kan ikke fortrydes.");

    if (actions > 0) {
      lines.push("", "Skriv eventets navn for at bekræfte:");
      const typed = prompt(lines.join("\n"));
      if (typed === null) return;
      if (typed.trim() !== ev.name.trim()) {
        alert("Navnet matcher ikke — eventet er ikke slettet.");
        return;
      }
    } else if (!confirm(lines.join("\n"))) {
      return;
    }

    await api.delete(`/api/events/${ev.id}`);
    navigate("/admin", { replace: true });
  }

  // Flytter en kamp op eller ned i listen og re-nummererer ALLE kampe
  // sekventielt (0, 1, 2, ...) så orderIndex altid er rent.
  async function move(fId: string, direction: "up" | "down") {
    if (!ev) return;
    const idx = ev.fights.findIndex((f) => f.id === fId);
    if (idx === -1) return;
    if (direction === "up" && idx === 0) return;
    if (direction === "down" && idx === ev.fights.length - 1) return;

    const next = [...ev.fights];
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    const a = next[idx];
    const b = next[swapIdx];
    if (!a || !b) return;
    next[idx] = b;
    next[swapIdx] = a;

    await Promise.all(
      next.map((f, i) =>
        api.patch(`/api/fights/${f.id}`, { orderIndex: i }),
      ),
    );
    load();
  }

  if (!ev) return <div className="p-6">Indlæser…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-4">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-3">
            {ev.name}
            <EventStatusBadge status={ev.status} />
          </h1>
          <div className="text-sm text-neutral-400">
            {new Date(ev.date).toLocaleString("da-DK")}
            {ev.location && ` · ${ev.location}`}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={`/admin/import?eventId=${ev.id}`}
            className="text-xs px-3 py-2 bg-neutral-700 hover:bg-neutral-600 rounded"
          >
            Opdatér fra ark
          </Link>
          <button
            onClick={startEditEvent}
            className="text-xs px-3 py-2 bg-neutral-700 hover:bg-neutral-600 rounded"
          >
            Rediger event
          </button>
          <button
            onClick={deleteEvent}
            className="text-xs px-3 py-2 bg-red-700 hover:bg-red-600 rounded"
          >
            Slet event
          </button>
          <Link
            to="/admin"
            className="text-sm text-neutral-400 hover:text-neutral-200 ml-2"
          >
            ← Events
          </Link>
        </div>
      </header>

      {eventForm && (
        <form
          onSubmit={saveEvent}
          className="bg-neutral-900 p-4 rounded-xl space-y-3 border border-neutral-800"
        >
          <h2 className="font-semibold">Rediger event</h2>
          <label className="flex flex-col gap-1 text-xs text-neutral-400">
            Navn
            <input
              value={eventForm.name}
              onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
              className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
            />
          </label>
          <div className="grid grid-cols-3 gap-2">
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Dato og tid
              <input
                type="datetime-local"
                value={eventForm.date}
                onChange={(e) => setEventForm({ ...eventForm, date: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Lokation
              <input
                value={eventForm.location}
                onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Status
              <select
                value={eventForm.status}
                onChange={(e) =>
                  setEventForm({ ...eventForm, status: e.target.value as EventStatus })
                }
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              >
                {(Object.keys(EVENT_STATUS_LABELS) as EventStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {EVENT_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
              <span className="text-neutral-500">
                Bliver automatisk Afsluttet når alle kampe er afsluttet.
                Arkiveret fryser ikke deploy.
              </span>
            </label>
          </div>
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={!eventForm.name.trim() || !eventForm.date}
              className="tap-btn px-4 py-2 bg-emerald-600 disabled:opacity-40"
            >
              Gem
            </button>
            <button
              type="button"
              onClick={() => setEventForm(null)}
              className="tap-btn px-4 py-2 bg-neutral-700"
            >
              Annullér
            </button>
          </div>
        </form>
      )}

      <button onClick={startNew} className="tap-btn px-4 py-2 bg-blue-600 text-sm">
        + Tilføj kamp
      </button>

      {editing && (
        <form onSubmit={save} className="bg-neutral-900 p-4 rounded-xl space-y-3 border border-neutral-800">
          <h2 className="font-semibold">{editing === "new" ? "Ny kamp" : "Rediger kamp"}</h2>
          <FighterFields
            fighters={fighters}
            label="Fighter A (blå hjørne)"
            prefix="A"
            value={{
              name: form.fighterAName,
              corner: form.fighterACorner,
              country: form.fighterACountry,
              club: form.fighterAClub,
              record: form.fighterARecord,
              weighIn: form.fighterAWeighIn,
            }}
            onChange={(v) =>
              setForm((s) => ({
                ...s,
                fighterAName: v.name,
                fighterACorner: v.corner,
                fighterACountry: v.country,
                fighterAClub: v.club,
                fighterARecord: v.record,
                fighterAWeighIn: v.weighIn,
              }))
            }
          />
          <FighterFields
            fighters={fighters}
            label="Fighter B (rød hjørne)"
            prefix="B"
            value={{
              name: form.fighterBName,
              corner: form.fighterBCorner,
              country: form.fighterBCountry,
              club: form.fighterBClub,
              record: form.fighterBRecord,
              weighIn: form.fighterBWeighIn,
            }}
            onChange={(v) =>
              setForm((s) => ({
                ...s,
                fighterBName: v.name,
                fighterBCorner: v.corner,
                fighterBCountry: v.country,
                fighterBClub: v.club,
                fighterBRecord: v.record,
                fighterBWeighIn: v.weighIn,
              }))
            }
          />
          <div className="grid grid-cols-3 gap-2">
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Vægtklasse
              <input
                placeholder="fx Lightweight (-70 kg)"
                value={form.weightClass}
                onChange={(e) => setForm({ ...form, weightClass: e.target.value })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Antal runder
              <input
                type="number"
                min={1}
                max={7}
                value={form.totalRounds}
                onChange={(e) => setForm({ ...form, totalRounds: Number(e.target.value) })}
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs text-neutral-400">
              Rundens længde (min)
              <input
                type="number"
                min={1}
                max={15}
                step={1}
                value={Math.round(form.roundDurationSec / 60)}
                onChange={(e) =>
                  setForm({ ...form, roundDurationSec: Number(e.target.value) * 60 })
                }
                className="bg-neutral-800 rounded px-3 py-2 text-base text-neutral-100"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="tap-btn px-4 py-2 bg-emerald-600">
              Gem
            </button>
            <button
              type="button"
              onClick={() => setEditing(null)}
              className="tap-btn px-4 py-2 bg-neutral-700"
            >
              Annullér
            </button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {ev.fights.map((f, i) => (
          <div
            key={f.id}
            className="bg-neutral-900 rounded-xl p-4 border border-neutral-800 flex items-center gap-3"
          >
            <span className="text-xs text-neutral-500 w-8">#{i + 1}</span>
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => move(f.id, "up")}
                disabled={i === 0}
                className="text-xs px-1.5 py-0.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed rounded"
                title="Flyt op"
              >
                ↑
              </button>
              <button
                onClick={() => move(f.id, "down")}
                disabled={i === ev.fights.length - 1}
                className="text-xs px-1.5 py-0.5 bg-neutral-800 hover:bg-neutral-700 disabled:opacity-30 disabled:cursor-not-allowed rounded"
                title="Flyt ned"
              >
                ↓
              </button>
            </div>
            <div className="flex-1">
              <div className="font-semibold">
                <FighterLink fighter={f.fighterA} />{" "}
                <span className="text-neutral-500">vs</span>{" "}
                <FighterLink fighter={f.fighterB} />
              </div>
              <div className="text-xs text-neutral-400">
                {f.weightClass ?? "—"} · {f.totalRounds}×{Math.round(f.roundDurationSec / 60)} min
                {f.level && ` · ${f.level === "pro" ? "Pro" : "Amatør"}`}
                {f.startTime && ` · kl. ${f.startTime}`}
                {f.sponsor && ` · Sponsor: ${f.sponsor}`}
              </div>
              {[
                [f.fighterA, tapeLine(f.fighterA.heightCm, f.fighterA.reachCm, f.fighterAWeighInKg)] as const,
                [f.fighterB, tapeLine(f.fighterB.heightCm, f.fighterB.reachCm, f.fighterBWeighInKg)] as const,
              ]
                .filter(([, line]) => line)
                .map(([fighter, line]) => (
                  <div key={fighter?.id} className="text-xs text-neutral-500">
                    {fighter?.name}: {line}
                  </div>
                ))}
            </div>
            <span
              className={`text-xs uppercase px-2 py-1 rounded ${
                f.status === "live"
                  ? "bg-amber-600 text-white"
                  : f.status === "finished"
                  ? "bg-neutral-700 text-neutral-300"
                  : "bg-neutral-800 text-neutral-400"
              }`}
            >
              {f.status}
            </span>
            <div className="flex flex-col gap-1">
              {f.status !== "live" && (
                <button
                  onClick={() => activate(f.id)}
                  className="text-xs px-2 py-1 bg-blue-600 rounded"
                >
                  Aktiver
                </button>
              )}
              {f.status === "live" && (
                <button
                  onClick={() => finish(f.id)}
                  className="text-xs px-2 py-1 bg-amber-600 rounded"
                >
                  Afslut
                </button>
              )}
              <button
                onClick={() => startEdit(f)}
                className="text-xs px-2 py-1 bg-neutral-700 rounded"
              >
                Rediger
              </button>
              <Link
                to={`/history/fights/${f.id}`}
                className="text-xs px-2 py-1 bg-neutral-700 rounded text-center"
              >
                Stats
              </Link>
              <button
                onClick={() => reset(f.id)}
                className="text-xs px-2 py-1 bg-amber-700 rounded"
                title="Slet alle stats og start kampen forfra"
              >
                Nulstil
              </button>
              <button
                onClick={() => del(f.id)}
                className="text-xs px-2 py-1 bg-red-700 rounded"
              >
                Slet
              </button>
            </div>
          </div>
        ))}
        {ev.fights.length === 0 && (
          <div className="text-neutral-500 text-sm">Ingen kampe endnu.</div>
        )}
      </div>
    </div>
  );
}

function FighterLink({ fighter }: { fighter: FighterProfile }) {
  return (
    <Link to={`/admin/fighters/${fighter.id}`} className="hover:underline">
      {fighter.name}
    </Link>
  );
}

function FighterFields({
  fighters,
  label,
  prefix,
  value,
  onChange,
}: {
  fighters: FighterOption[];
  label: string;
  prefix: string;
  value: { name: string; corner: "red" | "blue"; country: string; club: string; record: string; weighIn: string };
  onChange: (v: typeof value) => void;
}) {
  const known = fighters.find((f) => sameName(f.name, value.name));
  return (
    <fieldset className="border border-neutral-800 rounded-lg p-3 space-y-2">
      <legend className="text-sm px-2 text-neutral-400">{label}</legend>
      <input
        placeholder={`Navn ${prefix}`}
        value={value.name}
        list={`fighters-${prefix}`}
        onChange={(e) => {
          const name = e.target.value;
          // Valgt en kendt fighter: hent klub og land fra profilen.
          const match = fighters.find((f) => sameName(f.name, name));
          onChange(
            match
              ? { ...value, name, club: match.club ?? "", country: match.country ?? "" }
              : { ...value, name },
          );
        }}
        className="w-full bg-neutral-800 rounded px-3 py-2"
      />
      <datalist id={`fighters-${prefix}`}>
        {fighters.map((f) => (
          <option key={f.id} value={f.name}>
            {[f.club, f.country].filter(Boolean).join(" · ")}
          </option>
        ))}
      </datalist>
      <div className="text-xs text-neutral-500">
        {value.name.trim() === ""
          ? "Skriv navnet — kendte fightere foreslås"
          : known
            ? `Kendt fighter · ${known.fightCount} ${known.fightCount === 1 ? "kamp" : "kampe"} — klub og land opdaterer profilen`
            : "Ny fighter — oprettes når du gemmer"}
      </div>
      <div className="grid grid-cols-5 gap-2">
        <select
          value={value.corner}
          onChange={(e) => onChange({ ...value, corner: e.target.value as "red" | "blue" })}
          className="bg-neutral-800 rounded px-3 py-2"
        >
          <option value="red">Rød</option>
          <option value="blue">Blå</option>
        </select>
        <input
          placeholder="Land"
          value={value.country}
          onChange={(e) => onChange({ ...value, country: e.target.value })}
          className="bg-neutral-800 rounded px-3 py-2"
        />
        <input
          placeholder="Klub"
          value={value.club}
          onChange={(e) => onChange({ ...value, club: e.target.value })}
          className="bg-neutral-800 rounded px-3 py-2"
        />
        <input
          placeholder="Record (W-L-D)"
          value={value.record}
          onChange={(e) => onChange({ ...value, record: e.target.value })}
          className="bg-neutral-800 rounded px-3 py-2"
        />
        <input
          placeholder="Indvejning (kg)"
          value={value.weighIn}
          inputMode="decimal"
          onChange={(e) => onChange({ ...value, weighIn: e.target.value })}
          className="bg-neutral-800 rounded px-3 py-2"
        />
      </div>
    </fieldset>
  );
}
